import { Component, useEffect, useRef, useSyncExternalStore, type ReactElement, type ReactNode } from 'react';
// Bound to the concrete Cloudflare provider on purpose: it is the only registered provider. The
// provider-agnostic WAFChallengeManager tracks interactive transitions (notifyInteractiveStarted/Ended,
// wasEverInteractive) but exposes no subscribable current-interactive snapshot; add one there and switch to it
// if a second WAFChallengeProvider is ever registered.
import { cloudflareTurnstileClearanceProvider as clearanceProvider } from '@/services/waf/cloudflare/CloudflareTurnstileClearanceProvider';
import { reloadPageForWAF } from '@/services/waf/reloadPageForWAF';
import { runGuarded } from '@/services/waf/runGuarded';
import {
  getWAFFailureSnapshot,
  registerWAFFailureSurface,
  subscribeWAFFailure,
  type WAFTerminalFailure,
} from '@/services/waf/failureStore';
import { recordWAFTelemetry } from '@/services/waf/telemetry';
import { WAFTelemetryOutcomeExtra, WAFTelemetryPhase } from '@/services/waf/types';
import { wafChallengeManager } from '@/services/waf/WAFChallengeManager';
import { log } from '@/logger/logger.ts';
import { WeaveThemeProvider } from '@/UI/WeaveThemeProvider/WeaveThemeProvider';
import { WAFFailureModalStandalone } from '@/UI/WAFFailureModal/WAFFailureModalStandalone';
import { WAFSlowNoticeStandalone } from '@/UI/WAFFailureModal/WAFSlowNoticeStandalone';
import { isFigmaEmbedContext } from '@/utils/auth.utils';

const logger = log.getLogger('WAFChallenge');

// Renders null on a child throw instead of tripping the app-wide ErrorBoundary (a full-page ErrorPage plus a
// spurious Sentry/RUM report) or, on the tool page's boundary-less root, unmounting the sibling clearance host
// with it. This modal is advisory and only renders when things are already degraded, so a render crash must
// degrade to "no modal", never "no app" or "no WAF recovery" — mirroring the clearance host's own boundary.
class WAFFailureModalErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    // A raw Error serializes to {} in the log middleware, so pass its name/message explicitly; guard the log
    // because a throw from componentDidCatch escapes to the parent (app-wide) boundary this class exists to avoid.
    runGuarded(() =>
      logger.error('WAF failure modal render crash', { errorName: error.name, errorMessage: error.message }),
    );
  }

  render(): ReactNode {
    return this.state.hasError ? null : this.props.children;
  }
}

/**
 * The app-global blocking surface for WAF recovery: the terminal-failure modal, and the slow notice while a
 * clearance keeps solving invisibly past WAF_SLOW_CLEARANCE_DELAY_MS. Mounted at the top of the tree beside the
 * clearance host (main.tsx and the tool-page host), above the app's own WeaveThemeProvider and
 * FigmaEmbedModeProvider — so a page-load challenge that terminally fails before <App> mounts still surfaces.
 * It therefore wraps itself in a WeaveThemeProvider and reads embed state context-free, mirroring
 * WAFInteractiveChallengeOverlay. disableCssBaseline: always mounted outside PostHogProvider, so a CssBaseline
 * here would rewrite global styles with flags-unresolved vars; the dialog is self-sufficient.
 */
export function WAFFailureModal(): ReactElement {
  return (
    <WAFFailureModalErrorBoundary>
      <WeaveThemeProvider isFigmaEmbedOverride={isFigmaEmbedContext()} disableCssBaseline>
        <WAFFailureModalContent />
      </WeaveThemeProvider>
    </WAFFailureModalErrorBoundary>
  );
}

function WAFFailureModalContent(): ReactElement | null {
  const failure = useSyncExternalStore(subscribeWAFFailure, getWAFFailureSnapshot);
  // Suppress only while the interactive overlay actually occupies the screen — an invisible clearance renders
  // nothing, so hiding beneath it would just flicker the modal out from under the user; a solve clears the
  // failure (recovery), a non-solve leaves it correctly showing.
  const isInteractiveClearanceActive = useSyncExternalStore(
    clearanceProvider.subscribe,
    clearanceProvider.getInteractiveSnapshot,
  );
  const isClearanceSlow = useSyncExternalStore(
    wafChallengeManager.subscribeSlowClearance,
    wafChallengeManager.isActiveClearanceSlow,
  );
  const isEmbedded = isFigmaEmbedContext();
  const emittedFailureRef = useRef<WAFTerminalFailure | null>(null);

  useEffect(() => registerWAFFailureSurface(), []);

  useEffect(() => {
    if (failure === null) {
      emittedFailureRef.current = null;
      return;
    }
    if (isInteractiveClearanceActive || emittedFailureRef.current === failure) {
      return;
    }
    emittedFailureRef.current = failure;
    recordWAFTelemetry({
      phase: WAFTelemetryPhase.FailureModal,
      outcome: WAFTelemetryOutcomeExtra.FailureModalShown,
      terminalOutcome: failure.outcome,
      detail: failure.detail,
    });
  }, [failure, isInteractiveClearanceActive]);

  if (isInteractiveClearanceActive) {
    return null;
  }

  if (failure) {
    return (
      <WAFFailureModalStandalone
        outcome={failure.outcome}
        detail={failure.detail}
        isEmbedded={isEmbedded}
        onReload={() => {
          recordWAFTelemetry({
            phase: WAFTelemetryPhase.FailureModal,
            outcome: WAFTelemetryOutcomeExtra.FailureModalReload,
            terminalOutcome: failure.outcome,
            detail: failure.detail,
          });
          reloadPageForWAF();
        }}
      />
    );
  }

  if (isClearanceSlow) {
    return (
      <WAFSlowNoticeStandalone
        isEmbedded={isEmbedded}
        onReload={() => {
          recordWAFTelemetry({
            phase: WAFTelemetryPhase.Clearance,
            outcome: WAFTelemetryOutcomeExtra.SlowNoticeReload,
          });
          reloadPageForWAF();
        }}
      />
    );
  }

  return null;
}
